<script setup lang="ts">
import { JsonForms } from '@jsonforms/vue'
import { vuetifyRenderers } from '@jsonforms/vue-vuetify'
import type { JsonFormsChangeEvent } from '@jsonforms/core'



// ── JSON Schema describing the form data ──────────────────────────────────────

const schema = {
    type: 'object',
    properties: {
        equipmentId: {
            type: 'string',
            title: "Identifiant de l'équipement",
        },
        equipmentType: {
            type: 'string',
            title: "Type d'équipement",
            enum: ['Tracteur', 'Moissonneuse-batteuse', 'Pulvérisateur', 'Semoir', 'Chargeur', 'Autre'],
        },
        date: {
            type: 'string',
            format: 'date',
            title: 'Date de l\'observation',
        },
        severity: {
            type: 'string',
            title: 'Niveau de sévérité',
            enum: ['Information', 'Avertissement', 'Critique'],
        },
        comment: {
            type: 'string',
            title: 'Commentaire',
            minLength: 5,
            maxLength: 500,
        },
        maintenanceRequired: {
            type: 'boolean',
            title: 'Maintenance requise',
        },
        estimatedCost: {
            type: 'number',
            title: 'Coût estimé (CAD)',
            minimum: 0,
        },
        technicianName: {
            type: 'string',
            title: 'Nom du technicien',
        },
    },
    required: ['equipmentId', 'equipmentType', 'date', 'severity', 'comment'],
}

// ── UI Schema describing the layout ──────────────────────────────────────────

const uischema = {
    type: 'VerticalLayout',
    elements: [
        {
            type: 'HorizontalLayout',
            elements: [
                {
                    type: 'Control',
                    scope: '#/properties/equipmentId',
                    options: { placeholder: 'ex: TRACT-001' },
                },
                {
                    type: 'Control',
                    scope: '#/properties/equipmentType',
                },
            ],
        },
        {
            type: 'HorizontalLayout',
            elements: [
                {
                    type: 'Control',
                    scope: '#/properties/date',
                },
                {
                    type: 'Control',
                    scope: '#/properties/severity',
                },
            ],
        },
        {
            type: 'Control',
            scope: '#/properties/comment',
            options: {
                multi: true,
            },
        },
        {
            type: 'HorizontalLayout',
            elements: [
                {
                    type: 'Control',
                    scope: '#/properties/maintenanceRequired',
                },
                {
                    type: 'Control',
                    scope: '#/properties/estimatedCost',
                    rule: {
                        effect: 'SHOW',
                        condition: {
                            scope: '#/properties/maintenanceRequired',
                            schema: { const: true },
                        },
                    },
                },
            ],
        },
        {
            type: 'Control',
            scope: '#/properties/technicianName',
        },
    ],
}

// ── State ─────────────────────────────────────────────────────────────────────

declare interface EquipmentComment {
    equipmentId?: string
    equipmentType?: string
    date?: string
    severity?: string
    comment?: string
    maintenanceRequired?: boolean
    estimatedCost?: number
    technicianName?: string
}

const renderers = [...vuetifyRenderers]

const formData = ref<EquipmentComment>({
    date: new Date().toISOString().split('T')[0],
    maintenanceRequired: false,
})
const formErrors = ref<unknown[]>([])
const isValid = computed(() => formErrors.value.length === 0)

const submittedEntries = ref<EquipmentComment[]>([])
const snackbarVisible = ref(false)
const snackbarMsg = ref('')

function onFormChange(event: JsonFormsChangeEvent) {
    formData.value = event.data as EquipmentComment
    formErrors.value = event.errors ?? []
}

function submitForm() {
    if (!isValid.value) return
    submittedEntries.value.unshift({ ...formData.value })
    snackbarMsg.value = 'Commentaire enregistré avec succès.'
    snackbarVisible.value = true
    // Reset form to defaults
    formData.value = {
        date: new Date().toISOString().split('T')[0],
        maintenanceRequired: false,
    }
}

function resetForm() {
    formData.value = {
        date: new Date().toISOString().split('T')[0],
        maintenanceRequired: false,
    }
    formErrors.value = []
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function severityColor(severity?: string) {
    if (severity === 'Critique') return 'error'
    if (severity === 'Avertissement') return 'warning'
    return 'info'
}

function severityIcon(severity?: string) {
    if (severity === 'Critique') return 'M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z'
    if (severity === 'Avertissement') return 'M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z'
    return 'M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z'
}
</script>

<template>
    <v-card class="mx-4 my-4 page-card" rounded="xl">

        <!-- Page header -->
        <div class="page-header px-5 pt-5 pb-3">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
                <div class="d-flex align-center" style="gap: 12px;">
                    <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
                        <v-icon icon="M15 5H18L16.5 7L15 5M5 2H19C20.11 2 21 2.9 21 4V20C21 21.11 20.11 22 19 22H5C3.9 22 3 21.11 3 20V4C3 2.9 3.9 2 5 2M5 4V8H19V4H5M5 20H19V10H5V20M7 12H17V14H7V12M7 16H17V18H7V16Z" size="26" />
                    </v-avatar>
                    <div>
                        <div class="text-h6 font-weight-bold" style="line-height: 1.2;">JSONForms — Démo</div>
                        <div class="text-caption text-medium-emphasis">
                            Prototype d'intégration de JSONForms avec Vuetify 3
                        </div>
                    </div>
                </div>
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-chip color="primary" variant="tonal" size="small" prepend-icon="M5.5,7A1.5,1.5 0 0,1 4,5.5A1.5,1.5 0 0,1 5.5,4A1.5,1.5 0 0,1 7,5.5A1.5,1.5 0 0,1 5.5,7M21.41,11.58L12.41,2.58C12.05,2.22 11.55,2 11,2H4C2.89,2 2,2.89 2,4V11C2,11.55 2.22,12.05 2.59,12.41L11.58,21.41C11.95,21.77 12.45,22 13,22C13.55,22 14.05,21.77 14.41,21.41L21.41,14.41C21.78,14.05 22,13.55 22,13C22,12.44 21.77,11.94 21.41,11.58Z">
                        @jsonforms/vue-vuetify 3.7
                    </v-chip>
                </div>
            </div>
        </div>

        <v-divider />

        <!-- Content -->
        <v-container fluid class="pa-4">
            <v-row>

                <!-- Left: Form -->
                <v-col cols="12" md="6">
                    <v-card variant="outlined" rounded="lg">
                        <v-card-title class="d-flex align-center" style="gap: 8px;">
                            <v-icon icon="M19,19V5H5V19H19M19,3A2,2 0 0,1 21,5V19C21,20.11 20.1,21 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3H19M16.7,9.35L15.7,10.35L13.65,8.3L14.65,7.3C14.86,7.08 15.21,7.08 15.42,7.3L16.7,8.58C16.92,8.79 16.92,9.14 16.7,9.35M7,14.94L13.06,8.88L15.12,10.94L9.06,17H7V14.94Z" color="primary" />
                            Nouveau commentaire
                        </v-card-title>
                        <v-card-subtitle class="pb-2">
                            Formulaire piloté par JSON Schema + UI Schema
                        </v-card-subtitle>
                        <v-divider />
                        <v-card-text>
                            <json-forms
                                :data="formData"
                                :schema="schema"
                                :uischema="uischema"
                                :renderers="renderers"
                                validation-mode="ValidateAndShow"
                                @change="onFormChange"
                            />
                        </v-card-text>
                        <v-divider />
                        <v-card-actions class="pa-4" style="gap: 8px;">
                            <v-btn
                                color="primary"
                                variant="flat"
                                rounded="pill"
                                prepend-icon="M15,9H5V5H15M12,19A3,3 0 0,1 9,16A3,3 0 0,1 12,13A3,3 0 0,1 15,16A3,3 0 0,1 12,19M17,3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V7L17,3Z"
                                :disabled="!isValid"
                                @click="submitForm"
                            >
                                Enregistrer
                            </v-btn>
                            <v-btn
                                variant="tonal"
                                rounded="pill"
                                prepend-icon="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
                                @click="resetForm"
                            >
                                Réinitialiser
                            </v-btn>
                            <v-spacer />
                            <v-chip
                                :color="isValid ? 'success' : 'error'"
                                variant="tonal"
                                size="small"
                                :prepend-icon="isValid ? 'M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z' : 'M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z'"
                            >
                                {{ isValid ? 'Valide' : `${formErrors.length} erreur(s)` }}
                            </v-chip>
                        </v-card-actions>
                    </v-card>
                </v-col>

                <!-- Right: Raw data + submitted entries -->
                <v-col cols="12" md="6">

                    <!-- Raw JSON preview -->
                    <v-card variant="outlined" rounded="lg" class="mb-4">
                        <v-card-title class="d-flex align-center" style="gap: 8px;">
                            <v-icon icon="M5,3H7V5H5V10A2,2 0 0,1 3,12A2,2 0 0,1 5,14V19H7V21H5C3.93,20.73 3,20.1 3,19V15A2,2 0 0,0 1,13H0V11H1A2,2 0 0,0 3,9V5A2,2 0 0,1 5,3M19,3A2,2 0 0,1 21,5V9A2,2 0 0,0 23,11H24V13H23A2,2 0 0,0 21,15V19A2,2 0 0,1 19,21H17V19H19V14A2,2 0 0,1 21,12A2,2 0 0,1 19,10V5H17V3H19M12,15A1,1 0 0,1 13,16A1,1 0 0,1 12,17A1,1 0 0,1 11,16A1,1 0 0,1 12,15M8,15A1,1 0 0,1 9,16A1,1 0 0,1 8,17A1,1 0 0,1 7,16A1,1 0 0,1 8,15M16,15A1,1 0 0,1 17,16A1,1 0 0,1 16,17A1,1 0 0,1 15,16A1,1 0 0,1 16,15Z" color="secondary" />
                            Données brutes (temps réel)
                        </v-card-title>
                        <v-card-text class="pa-0">
                            <v-sheet color="surface-variant" rounded="b-lg" class="pa-4">
                                <pre style="font-size: 0.75rem; white-space: pre-wrap; word-break: break-word;">{{
                                    JSON.stringify(formData, null, 2)
                                }}</pre>
                            </v-sheet>
                        </v-card-text>
                    </v-card>

                    <!-- Submitted entries -->
                    <v-card variant="outlined" rounded="lg">
                        <v-card-title class="d-flex align-center" style="gap: 8px;">
                            <v-icon icon="M19 3H14.82C14.4 1.84 13.3 1 12 1S9.6 1.84 9.18 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3M12 3C12.55 3 13 3.45 13 4S12.55 5 12 5 11 4.55 11 4 11.45 3 12 3M7 7H17V5H19V19H5V5H7V7M12 17V15H17V17H12M12 11V9H17V11H12M8 12V9H7V8H9V12H8M9.25 14C9.66 14 10 14.34 10 14.75C10 14.95 9.92 15.14 9.79 15.27L8.12 17H10V18H7V17.08L9 15H7V14H9.25" color="primary" />
                            Commentaires soumis
                            <v-chip class="ms-2" size="x-small" color="primary" variant="tonal">
                                {{ submittedEntries.length }}
                            </v-chip>
                        </v-card-title>
                        <v-divider />
                        <v-card-text v-if="submittedEntries.length === 0" class="text-center text-medium-emphasis py-6">
                            <v-icon icon="M19 3H5A2 2 0 0 0 3 5V19A2 2 0 0 0 5 21H19A2 2 0 0 0 21 19V5A2 2 0 0 0 19 3M5 19V17H8.13A4.13 4.13 0 0 0 9.4 19M19 19H14.6A4.13 4.13 0 0 0 15.87 17H19M19 15H14V16A2 2 0 0 1 10 16V15H5V5H19Z" size="40" class="mb-2 d-block" />
                            Aucun commentaire soumis pour l'instant.
                        </v-card-text>
                        <v-list v-else lines="three" class="pa-2">
                            <v-list-item
                                v-for="(entry, i) in submittedEntries"
                                :key="i"
                                rounded="lg"
                                class="mb-2"
                                :subtitle="`${entry.equipmentId} — ${entry.date}`"
                                :title="entry.equipmentType ?? '—'"
                            >
                                <template #prepend>
                                    <v-avatar :color="severityColor(entry.severity)" variant="tonal" size="38" rounded="lg">
                                        <v-icon :icon="severityIcon(entry.severity)" size="22" />
                                    </v-avatar>
                                </template>
                                <template #append>
                                    <v-chip
                                        :color="severityColor(entry.severity)"
                                        size="x-small"
                                        variant="tonal"
                                    >
                                        {{ entry.severity }}
                                    </v-chip>
                                </template>
                                <div class="text-body-2 mt-1 text-medium-emphasis" style="white-space: pre-wrap;">
                                    {{ entry.comment }}
                                </div>
                                <div v-if="entry.maintenanceRequired" class="d-flex align-center mt-1" style="gap: 4px;">
                                    <v-icon icon="M22.7,19L13.6,9.9C14.5,7.6 14,4.9 12.1,3C10.1,1 7.1,0.6 4.7,1.7L9,6L6,9L1.6,4.7C0.4,7.1 0.9,10.1 2.9,12.1C4.8,14 7.5,14.5 9.8,13.6L18.9,22.7C19.3,23.1 19.9,23.1 20.3,22.7L22.6,20.4C23.1,20 23.1,19.3 22.7,19Z" size="14" color="warning" />
                                    <span class="text-caption text-warning">
                                        Maintenance requise
                                        <template v-if="entry.estimatedCost">
                                            — {{ entry.estimatedCost.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' }) }}
                                        </template>
                                    </span>
                                </div>
                            </v-list-item>
                        </v-list>
                    </v-card>

                </v-col>
            </v-row>
        </v-container>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbarVisible" color="success" rounded="pill" :timeout="3000">
            <v-icon icon="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" class="me-2" />
            {{ snackbarMsg }}
        </v-snackbar>

    </v-card>
</template>
